import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Create';
import styles from './SavedSearchCard.scss';
import EditSavedSearchModal from '../Modal/EditSavedSearchModal/EditSavedSearchModal';
import {closeModal, showModal, updateSavedSearch} from '../../actions';

const Attributes = (props) => {
  const {
    bedsRange,
    bathsRange,
    carSpacesRange,
  } = props;

  return (
    <React.Fragment>
      <div className="d-flex align-items-center" title="Bedrooms">
        <div className={`icon mr-2 ${styles.beds}`}/>
        <div className="mr-3">{bedsRange || 'Any'}</div>
      </div>
      <div className="d-flex align-items-center" title="Bathrooms">
        <div className={`icon mr-2 ${styles.baths}`}/>
        <div className="mr-3">{bathsRange || 'Any'}</div>
      </div>
      <div className="d-flex align-items-center" title="Car spaces">
        <div className={`icon mr-2 ${styles.carSpaces}`}/>
        <div className="mr-3">{carSpacesRange || 'Any'}</div>
      </div>
    </React.Fragment>
  );
};

const EmailAlerts = (props) => {
  const {
    alertFrequency,
    searchId,
    name,
    query,
    sort,
  } = props;

  const options = [
    {label: 'Daily', value: 'Daily'},
    {label: 'Weekly', value: 'Weekly'},
    {label: 'Monthly', value: 'Monthly'},
    {label: 'Never', value: 'Never'},
  ];

  const handleAlertFrequencyChange = (event) => {
    const {value} = event?.target;
    const payload = {
      alertFrequency: value,
      searchId,
      name,
      query,
      sort,
      isAlertFrequencyUpdate: true,
    };

    props.updateSavedSearch(payload);
  };

  return (
    <div className="d-flex flex-column">
      <span className="mb-2">Get Email Alerts</span>
      <div className="d-flex flex-wrap">
        {options.map(({label, value}) => (
          <label className={`d-flex mr-3 align-items-center ${styles.alertFrequency}`} key={label}>
            <input
              className="mr-2"
              type="radio"
              name={`alertFrequency-${searchId}`}
              value={value}
              checked={alertFrequency === value}
              onChange={handleAlertFrequencyChange}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

function SavedSearchCard(props) {
  const {
    searchURL,
    name,
    location,
    searchId,
    removeSearch,
    alertFrequency,
    query,
    sort,
    searchData: {
      types,
      status,
      bedsRange,
      bathsRange,
      carSpacesRange,
      priceRange,
      moreFilters,
      includeSurroundingAreas,
    },
  } = props;

  const handleEditClick = (event) => {
    event.preventDefault();
    props.showModal({
      open: true,
      title: 'Edit Saved Search Name',
      component: <EditSavedSearchModal
        name={name}
        query={query}
        sort={sort}
        alertFrequency={alertFrequency}
        searchId={searchId}
        closeModal={props.closeModal}
        updateSavedSearch={props.updateSavedSearch}
      />
    });
  };

  return (
    <div className={'card border shadow-sm mb-5 p-4 ' + styles.card}>
      <div className={'d-flex flex-row flex-grow-1 pb-3 mb-3 ' + styles.header}>
        <span className={styles.address}>{name}</span>
        <EditIcon className={styles.editIcon} onClick={handleEditClick} />
        <div className={`ml-auto ${styles.viewResults}`}>
          <Link to={searchURL} target="_blank" rel="noopener noreferrer">
            <OpenInNewIcon className="mx-1"/>View Results
          </Link>
        </div>
      </div>
      <div className={'d-flex flex-column ' + styles.body}>
        <div className="mb-3">
          <div className="mb-3">
            {[
              status,
              location + (includeSurroundingAreas ? ' (and surrounding areas)' : ''),
              types.join(', '),
              priceRange || 'Any price'
            ].filter(each => each).join(' | ')}
          </div>
          <div className="d-flex">
            <Attributes
              bedsRange={bedsRange}
              bathsRange={bathsRange}
              carSpacesRange={carSpacesRange}
            />
          </div>
          {moreFilters.length > 0 &&
            <div className="mt-3">
              More filters:&nbsp;
              <span className={styles.moreFilters}>{moreFilters.join(', ')}</span>
            </div>
          }
        </div>
        <div className="d-flex">
          {(!['Off-market', 'All properties'].includes(status) && query?.queries?.length === 1) &&
            <EmailAlerts
              alertFrequency={alertFrequency}
              searchId={searchId}
              name={name}
              query={query}
              sort={sort}
              updateSavedSearch={props.updateSavedSearch}
            />
          }
          <div
            className={'ml-auto d-flex align-items-end ' + styles.deleteLink}
            onClick={() => removeSearch(searchId)}
            title="Delete Search"
          >
            <DeleteOutlineIcon className="mx-1" />
            <span>Delete Saved Search</span>
          </div>
        </div>
      </div>
    </div>
  );
}

SavedSearchCard.propTypes = {
  searchURL: PropTypes.string,
  name: PropTypes.string,
  location: PropTypes.string,
  searchId: PropTypes.string,
  removeSearch: PropTypes.func,
  alertFrequency: PropTypes.string,
  searchData: PropTypes.object,
  query: PropTypes.object,
  sort: PropTypes.array,
  showModal: PropTypes.func,
  closeModal: PropTypes.func,
  updateSavedSearch: PropTypes.func,
};

EmailAlerts.propTypes = {
  alertFrequency: PropTypes.string,
  searchId: PropTypes.string,
  name: PropTypes.string,
  query: PropTypes.object,
  sort: PropTypes.array,
  updateSavedSearch: PropTypes.func,
};

Attributes.propTypes = {
  bedsRange: PropTypes.string,
  bathsRange: PropTypes.string,
  carSpacesRange: PropTypes.string,
};

const mapDispatchToProps = {
  showModal,
  closeModal,
  updateSavedSearch,
};

export default connect(null, mapDispatchToProps)(SavedSearchCard);
